import { useEffect, useState } from "react";
import { initializeApp } from "firebase/app";
import {
  getFirestore,
  collection,
  addDoc,
  doc,
  updateDoc,
  onSnapshot,
  increment,
  query,
  orderBy,
} from "firebase/firestore";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

type Sugestao = {
  id: string;
  name: string;
  votes: number;
};

const firebaseConfig = {
  apiKey: "AIzaSyAu-bCDoz3y6DKJkDGOp9nDMmLJgrz9f00",
  authDomain: "enquete-tp.firebaseapp.com",
  projectId: "enquete-tp",
  storageBucket: "enquete-tp.firebasestorage.app",
  messagingSenderId: "964275394951",
  appId: "1:964275394951:web:deebb4e1d80d7f19f35b22",
  measurementId: "G-CHJDWSYRRJ",
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

export default function Sugestoes() {
  const [suggestion, setSuggestion] = useState("");
  const [sugestoes, setSugestoes] = useState<Sugestao[]>([]);
  const [votedSugestoes, setVotedSugestoes] = useState<string[]>([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const sugestoesCollection = collection(db, "sugestoes");
    const unsub = onSnapshot(
      query(sugestoesCollection, orderBy("votes", "desc")),
      (snapshot) => {
        const data: Sugestao[] = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...(doc.data() as Omit<Sugestao, "id">),
        }));
        setSugestoes(data);
      }
    );

    const voted = JSON.parse(localStorage.getItem("votedSugestoes") || "[]");
    setVotedSugestoes(voted);

    return () => unsub();
  }, []);

  const handleSuggest = async () => {
    const trimmed = suggestion.trim();
    if (!trimmed) return;

    const exists = sugestoes.find(
      (s) => s.name.toLowerCase() === trimmed.toLowerCase()
    );
    if (exists) {
      alert("Esta sugestão já existe!");
      return;
    }

    const sugestoesCollection = collection(db, "sugestoes");
    const docRef = await addDoc(sugestoesCollection, {
      name: trimmed,
      votes: 1,
    });

    const newVoted = [...votedSugestoes, docRef.id];
    setVotedSugestoes(newVoted);
    localStorage.setItem("votedSugestoes", JSON.stringify(newVoted));
    setSuggestion("");
    alert("Sugestão enviada com sucesso!");
  };

  const handleVote = async (id: string) => {
    if (votedSugestoes.includes(id)) return;

    const ref = doc(db, "sugestoes", id);
    await updateDoc(ref, {
      votes: increment(1),
    });

    const newVoted = [...votedSugestoes, id];
    setVotedSugestoes(newVoted);
    localStorage.setItem("votedSugestoes", JSON.stringify(newVoted));
  };

  const filteredSugestoes = sugestoes
    .filter((s) => s.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      // Se ambos têm 0 votos, ordena alfabeticamente
      if (a.votes === 0 && b.votes === 0) {
        return a.name.localeCompare(b.name, "pt-BR");
      }
      // Se apenas um tem 0 votos, coloca o com 0 votos depois
      if (a.votes === 0) return 1;
      if (b.votes === 0) return -1;
      // Se ambos têm votos, ordena por número de votos (decrescente)
      return b.votes - a.votes;
    });

  return (
    <div className="max-w-xl mx-auto p-4 text-center">
      <div className="flex items-center justify-between mb-4">
        <Link
          to="/"
          className="inline-flex items-center justify-center w-8 h-8 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-full transition-colors duration-200"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <h1 className="text-2xl font-bold flex-1">Sugestões</h1>
        <div className="w-8"></div>
      </div>

      <p className="mb-6">
        Sugira perfumes que você gostaria de ver na Tabs ou vote nas sugestões
        de outros clientes!
      </p>

      {/* Formulário de Sugestão */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <h2 className="text-lg font-semibold mb-3">Sugerir Novo Perfume</h2>
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={suggestion}
            onChange={(e) => setSuggestion(e.target.value)}
            placeholder="Ex: Lattafa - Asad, Carolina Herrera - Good Girl..."
            className="flex-1 border rounded px-3 py-2"
          />
          <button
            onClick={handleSuggest}
            className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800"
          >
            Sugerir
          </button>
        </div>
        <p className="text-sm text-gray-600">
          💡 Verifique se o perfume já não foi sugerido por outro cliente.
        </p>
      </div>

      {/* Lista de Sugestões */}
      <div className="mb-4">
        <h2 className="text-lg font-semibold mb-3">Votar em Sugestões</h2>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar sugestão..."
          className="w-full border rounded px-3 py-2"
        />
      </div>

      <ul className="space-y-3">
        {filteredSugestoes.map((s) => (
          <li
            key={s.id}
            className="flex justify-between items-center border rounded px-4 py-2"
          >
            <span className="text-left w-2/3">{s.name}</span>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">{s.votes} voto(s)</span>
              <button
                onClick={() => handleVote(s.id)}
                disabled={votedSugestoes.includes(s.id)}
                className={`px-3 py-1 rounded text-sm ${
                  votedSugestoes.includes(s.id)
                    ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                    : "bg-green-500 text-white hover:bg-green-600"
                }`}
              >
                {votedSugestoes.includes(s.id) ? "Votado" : "Votar"}
              </button>
            </div>
          </li>
        ))}
      </ul>

      {filteredSugestoes.length === 0 && (
        <p className="text-gray-500 mt-6">
          Nenhuma sugestão encontrada. Seja o primeiro a sugerir um perfume!
        </p>
      )}
    </div>
  );
}
